import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Divider, Stack } from '@mui/material';
import useAuth from 'hooks/useAuth';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import DialogClose from 'views/components/buttons/DialogClose';
import FileUploader from 'views/components/inputs/FileUploader';
import FormContainer from 'views/components/inputs/FormContainer';
import TextField from 'views/components/inputs/TextField';
import * as Yup from 'yup';

interface Props extends DialogProps {
  userId: string;
  onSubmitSuccess: () => void;
  onClose: () => void;
}

export type UploadDocumentValues = any;

export default function UploadDocumentDialog({ userId, onSubmitSuccess, onClose, ...props }: Props) {
  const intl = useIntl();

  const validationSchema = Yup.object().shape({
    fileName: Yup.string().required(intl.formatMessage({ defaultMessage: 'Required' }))
    // fileDescription: Yup.string()
  });

  const { request } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const uploadDocumentMutation = useMutation(
    (variables: any) => request.post(`user/${userId}/documents`, variables, { headers: { 'content-type': 'multipart/form-data' } }),
    {
      onSuccess: onSubmitSuccess
    }
  );

  return (
    <Dialog maxWidth="sm" fullWidth sx={{ '& .MuiDialog-paper': { p: 0 } }} onClose={onClose} {...props}>
      <FormContainer
        validation={validationSchema}
        defaultValues={{ fileName: '', fileDescription: '' }}
        onSuccess={async (newValues: UploadDocumentValues) => {
          if (!file) {
            return;
          }
          const formData = new FormData();
          formData.append('file', file);
          formData.append('fileName', newValues.fileName);
          // formData.append('fileDescription', newValues.fileDescription ?? '');

          await uploadDocumentMutation.mutateAsync(formData);
          onClose();
        }}
      >
        <DialogTitle>
          <FormattedMessage defaultMessage="Upload Document" />
          <DialogClose onClose={onClose} />
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3}>
            <FileUploader
              label={intl.formatMessage({ defaultMessage: 'Select your document' })}
              description={intl.formatMessage({ defaultMessage: 'Select all your file you want to upload (Max. {size} MB)' }, { size: 25 })}
              file={file}
              onChange={setFile}
            />
            {file ? (
              <>
                <Divider />
                <TextField name="fileName" label={intl.formatMessage({ defaultMessage: 'File Name' })} required />
                {/* <TextField name="fileDescription" label={intl.formatMessage({ defaultMessage: 'File Description' })} /> */}
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button type="button" variant="outlined" onClick={onClose}>
              <FormattedMessage defaultMessage="Cancel" />
            </Button>
            <Button type="submit" variant="contained" disabled={!file || uploadDocumentMutation.isLoading}>
              <FormattedMessage defaultMessage="Upload Now" />
            </Button>
          </Stack>
        </DialogActions>
      </FormContainer>
    </Dialog>
  );
}
